import axios from 'axios';
import { splitEvery } from 'ramda';

import { prepareAssets } from 'apis/asset-repository-service/api';
import { AssetScope, PreparedAsset } from 'apis/asset-repository-service/types';
import useUser from 'common/hooks/useUser';

import { useScopeContext } from '../contexts/ScopeContext';

const MAX_UPLOAD_REQUEST = 5;

const isAssetScope = (scope: string): scope is AssetScope['type'] =>
  ['venue', 'merchant', 'brand', 'country', 'unspecified'].includes(scope);

export const uploadFileToUrl = async (file: File, uploadUrl: string) => {
  await axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  });
};

const uploadAssetsConcurrently = async (assets: PreparedAsset[], files: File[], maxRequestCount: number) => {
  const uploadResults: PromiseSettledResult<string>[] = [];
  const split = splitEvery(maxRequestCount, assets);

  const uploadChunks = split.map(
    chunk => () =>
      Promise.allSettled(
        chunk.map(async (asset, index) => {
          await uploadFileToUrl(files[index], asset.s3PresignedUrl);
          return asset.imageResizerProxyUrl;
        }),
      ),
  );

  const recursiveUploadChunk = async () => {
    const uploadAssets = uploadChunks.shift();

    if (!uploadAssets) {
      return;
    }

    const results = await uploadAssets();
    uploadResults.push(...results);

    await recursiveUploadChunk();
  };

  await recursiveUploadChunk();

  return uploadResults
    .filter(({ status }) => status === 'fulfilled')
    .map((result: PromiseFulfilledResult<string>) => result.value);
};

const useAssetRepositoryService = () => {
  const { isEmployee, user } = useUser();
  const scope = useScopeContext();

  const uploadAssets = async (assets: File[]) => {
    if (!user) {
      throw new Error('User must be authorized to upload assets');
    }

    const preparedAssets = await prepareAssets(
      assets.map(asset => ({
        contentType: asset.type,
        name: asset.name,
        scope: {
          type: isAssetScope(scope.type) ? scope.type : 'unspecified',
          scopeId: scope.id,
        },
        source: { type: isEmployee ? 'wolt_employee' : 'self_service', sourceId: user.id },
      })),
    );

    return await uploadAssetsConcurrently(preparedAssets, assets, MAX_UPLOAD_REQUEST);
  };

  const uploadAsset = async (asset: File) => (await uploadAssets([asset]))[0];

  return {
    uploadAssets,
    uploadAsset,
  };
};

export default useAssetRepositoryService;
